import React, { useCallback, useMemo } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormattedMessage, useIntl } from 'react-intl';

interface MarkerPoint {
  id: string;
  label: string;
  coordinate: [number, number];
}

interface MarkerPointListProps {
  markerPoints: MarkerPoint[];
  hoveredMarkerId?: string | null;
  onMarkerHover?: (id: string | null) => void;
  onMarkerPointsClear: () => void;
  onMarkerPointDelete: (id: string) => void;
}

interface MarkerPointRowProps {
  point: MarkerPoint;
  index: number;
  highlighted: boolean;
  onMarkerHover?: (id: string | null) => void;
  onMarkerPointDelete: (id: string) => void;
}

const markerPointRowStyles = {
  borderRadius: 1,
  transition: 'background-color 0.15s ease',
  '& .MuiIconButton-root': {
    visibility: 'hidden',
  },
  '&:hover .MuiIconButton-root': {
    visibility: 'visible',
  },
};

const markerPointRowHighlightedStyles = {
  borderRadius: 1,
  transition: 'background-color 0.15s ease',
  backgroundColor: 'action.hover',
  '& .MuiIconButton-root': {
    visibility: 'hidden',
  },
  '&:hover .MuiIconButton-root': {
    visibility: 'visible',
  },
};

const markerBoxStyle = {
  position: 'relative',
  height: '40px',
  width: '36px',
  backgroundImage:
    'url("https://acolyte-images.s3.eu-central-1.amazonaws.com/icon-atlas.png")',
  backgroundSize: '150px 150px',
  backgroundPosition: 'top right 112px',
};

const deleteAllButtonStyles = { padding: '4px' };
const deleteMarkerButtonStyles = { padding: '6px' };
const deleteMarkerIconStyles = { fontSize: '16px' };

const MarkerText = styled('span')`
  user-select: none;
  font-family: monospace;
  margin-top: 9px;
  color: #000;
`;

const MarkerImage = ({ index }: { index: number }) => (
  <Box sx={markerBoxStyle}>
    <Stack
      direction="row"
      position="absolute"
      width="100%"
      height="100%"
      justifyContent="center"
    >
      <MarkerText>{index + 1}</MarkerText>
    </Stack>
  </Box>
);

const MarkerPointRow = ({
  point,
  index,
  highlighted,
  onMarkerHover,
  onMarkerPointDelete,
}: MarkerPointRowProps) => {
  const intl = useIntl();
  const rowSx = useMemo(
    () =>
      highlighted ? markerPointRowHighlightedStyles : markerPointRowStyles,
    [highlighted]
  );

  const handleMouseEnter = useCallback(() => {
    onMarkerHover?.(point.id);
  }, [onMarkerHover, point.id]);

  const handleMouseLeave = useCallback(() => {
    onMarkerHover?.(null);
  }, [onMarkerHover]);

  const handleDelete = useCallback(() => {
    onMarkerPointDelete(point.id);
  }, [onMarkerPointDelete, point.id]);

  return (
    <Stack
      sx={rowSx}
      direction="row"
      alignItems="stretch"
      gap={1.5}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        width={45}
        height={70}
      >
        <MarkerImage index={index} />
      </Stack>

      <Stack flexGrow={1} alignSelf="center" justifyItems="flex-start">
        <Typography fontSize={14} fontWeight={600} variant="body1">
          {point.label}
        </Typography>
        <Typography
          lineHeight={1.2}
          fontSize={12}
          variant="body1"
          color="text.secondary"
        >
          {point.coordinate[1]}, {point.coordinate[0]}
        </Typography>
        <Typography
          lineHeight={1.2}
          fontSize={12}
          variant="body1"
          color="text.secondary"
        >
          <FormattedMessage
            id="map.marker.baseLayer"
            defaultMessage="base layer"
          />
        </Typography>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        flexGrow={0}
        width={28}
      >
        <IconButton
          onClick={handleDelete}
          sx={deleteMarkerButtonStyles}
          aria-label={intl.formatMessage({
            id: 'map.marker.delete',
            defaultMessage: 'Delete marker point',
          })}
        >
          <DeleteIcon sx={deleteMarkerIconStyles} />
        </IconButton>
      </Stack>
    </Stack>
  );
};

const MarkerPointList = ({
  markerPoints,
  hoveredMarkerId = null,
  onMarkerHover,
  onMarkerPointsClear,
  onMarkerPointDelete,
}: MarkerPointListProps) => {
  const intl = useIntl();

  return (
    <Stack gap={1} my={1}>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Typography fontWeight={500} fontSize={14}>
          <FormattedMessage
            id="map.marker.selectedPoints"
            defaultMessage="Selected points"
          />
        </Typography>
        <IconButton
          onClick={onMarkerPointsClear}
          sx={deleteAllButtonStyles}
          aria-label={intl.formatMessage({
            id: 'map.marker.clearAll',
            defaultMessage: 'Clear all marker points',
          })}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
      {markerPoints.map((point, index) => (
        <MarkerPointRow
          key={point.id}
          point={point}
          index={index}
          highlighted={point.id === hoveredMarkerId}
          onMarkerHover={onMarkerHover}
          onMarkerPointDelete={onMarkerPointDelete}
        />
      ))}
    </Stack>
  );
};

export { MarkerPointList };
